import { Helmet } from '@dr.pogodin/react-helmet';
import { useRouterState } from '@tanstack/react-router';

import { env } from '@shared/config/env';

const SITE_NAME = 'Узнали?';
const DEFAULT_DESCRIPTION = 'Короткие ежедневные квизы по фильмам, сериалам, аниме и видеоиграм.';
const NOINDEX_PATHS = [/^\/profile(?:\/|$)/, /^\/sign-(?:in|up)(?:\/|$)/];

export type SeoData = {
  title: string;
  description: string;
  canonicalPath?: string;
  robots?: 'index,follow' | 'noindex,follow' | 'noindex,nofollow';
  jsonLd?: Record<string, unknown>;
};

export const getRobotsForPath = (pathname: string) =>
  NOINDEX_PATHS.some((pattern) => pattern.test(pathname)) ? ('noindex,follow' as const) : ('index,follow' as const);

const getCanonicalUrl = (path: string) => new URL(path, `${env.siteUrl}/`).toString();

export const GlobalSeo = () => {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const canonicalUrl = getCanonicalUrl(pathname);

  return (
    <Helmet>
      <html lang="ru" />
      <title>{SITE_NAME}</title>
      <meta name="description" content={DEFAULT_DESCRIPTION} />
      <meta name="robots" content={getRobotsForPath(pathname)} />
      <link rel="canonical" href={canonicalUrl} />
      <meta property="og:locale" content="ru_RU" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={SITE_NAME} />
      <meta property="og:description" content={DEFAULT_DESCRIPTION} />
      <meta name="twitter:card" content="summary" />
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'WebSite',
          url: `${env.siteUrl}/`,
          name: SITE_NAME,
          inLanguage: 'ru-RU',
        }).replaceAll('<', '\\u003c')}
      </script>
    </Helmet>
  );
};

export const Seo = ({ title, description, canonicalPath, robots, jsonLd }: SeoData) => {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const canonicalUrl = getCanonicalUrl(canonicalPath ?? pathname);

  return (
    <Helmet>
      <title>{`${title} | ${SITE_NAME}`}</title>
      <meta name="description" content={description} />
      <meta name="robots" content={robots ?? getRobotsForPath(pathname)} />
      <link rel="canonical" href={canonicalUrl} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      {jsonLd ? <script type="application/ld+json">{JSON.stringify(jsonLd).replaceAll('<', '\\u003c')}</script> : null}
    </Helmet>
  );
};
